<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import type { Modificator } from '@/api/dx-backend'
import { statDeltasOf } from '@/utils/statDeltas'

interface Props {
  modificator: Modificator
}

const props = defineProps<Props>()

const deltas = computed(() => statDeltasOf(props.modificator.stat_changes))

const tipId = computed(() => `mod-tip-${props.modificator.id}`)
const open = ref(false)
const tag = ref<HTMLElement | null>(null)
const tip = ref<HTMLElement | null>(null)
const position = ref({ top: 0, left: 0 })

/*
 * The readout is fixed-positioned in a portal rather than absolute inside the
 * tag: the dossier is a clipped, scrolling panel and the identity column is only
 * 15.5rem wide, so a readout parented to the tag would be cut off on both axes.
 */
const GUTTER = 8

const place = () => {
  const anchor = tag.value?.getBoundingClientRect()
  const box = tip.value?.getBoundingClientRect()
  if (!anchor || !box) return

  const above = anchor.top - box.height - GUTTER
  const left = anchor.left + anchor.width / 2 - box.width / 2

  position.value = {
    top: above >= GUTTER ? above : anchor.bottom + GUTTER,
    left: Math.min(Math.max(left, GUTTER), window.innerWidth - box.width - GUTTER),
  }
}

const show = () => { open.value = true }
const hide = () => { open.value = false }

const onKeydown = (event: KeyboardEvent) => {
  /* Escape dismisses the readout without reaching the dossier behind it, so a
     player reading a modifier does not lose the whole panel to one keypress. */
  if (event.key !== 'Escape' || !open.value) return
  event.stopPropagation()
  hide()
  tag.value?.focus()
}

watch(open, async (isOpen) => {
  if (!isOpen) {
    window.removeEventListener('scroll', hide, true)
    window.removeEventListener('resize', hide)
    return
  }
  await nextTick()
  place()
  window.addEventListener('scroll', hide, true)
  window.addEventListener('resize', hide)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', hide, true)
  window.removeEventListener('resize', hide)
})
</script>

<template>
  <li class="mod-tag-item">
    <span
        ref="tag"
        class="mod-tag"
        :class="{ 'is-open': open }"
        tabindex="0"
        :aria-describedby="open ? tipId : undefined"
        @mouseenter="show"
        @mouseleave="hide"
        @focus="show"
        @blur="hide"
        @keydown="onKeydown"
    >{{ modificator.name }}</span>

    <Teleport to="body">
      <Transition name="mod-tip-fade">
        <div
            v-if="open"
            :id="tipId"
            ref="tip"
            class="mod-tip"
            role="tooltip"
            :style="{ top: `${position.top}px`, left: `${position.left}px` }"
        >
          <p class="mod-tip__line">
            <span class="mod-tip__prompt">&gt;</span>
            <span class="mod-tip__name">{{ modificator.name }}</span>
            <span class="mod-tip__caret" aria-hidden="true"></span>
          </p>

          <p v-if="modificator.description" class="mod-tip__prose">{{ modificator.description }}</p>

          <ul v-if="deltas.length > 0" class="mod-tip__rows">
            <li v-for="delta in deltas" :key="delta.key" class="mod-tip__row">
              <span class="mod-tip__stat">{{ delta.stat }}</span>
              <span class="mod-tip__leader" aria-hidden="true"></span>
              <span class="mod-tip__value" :class="delta.positive ? 'is-up' : 'is-down'">
                {{ delta.sign }}{{ delta.magnitude }}
              </span>
            </li>
          </ul>
        </div>
      </Transition>
    </Teleport>
  </li>
</template>

<style scoped>
.mod-tag-item {
  display: flex;
}

.mod-tag {
  padding: 0.15rem 0.4rem;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.55rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--hud-cyan, #3ee9f0);
  background: rgba(62, 233, 240, 0.07);
  border: 1px solid var(--hud-line, rgba(0, 255, 255, 0.18));
  border-radius: 0.15rem;
  cursor: help;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
}

.mod-tag:hover,
.mod-tag.is-open {
  background: rgba(62, 233, 240, 0.13);
  border-color: var(--hud-cyan-dim, rgba(62, 233, 240, 0.45));
  box-shadow: 0 0 0.6rem rgba(62, 233, 240, 0.22);
}

.mod-tag:focus-visible {
  outline: 1px solid var(--hud-cyan, #3ee9f0);
  outline-offset: 2px;
}

/* ---- the readout ---- */

.mod-tip {
  --tip-cyan: #3ee9f0;
  --tip-up: #00ff6a;
  --tip-down: #ff1744;

  position: fixed;
  /* Above the dossier overlay (9000) and the base-swap modal it can open (9100),
     since the readout is only ever on screen while the dossier is. */
  z-index: 9200;
  width: max-content;
  max-width: 15rem;
  padding: 0.35rem 0.5rem 0.4rem;
  font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  font-size: 0.56rem;
  line-height: 1.5;
  color: var(--tip-cyan);
  background: rgba(4, 10, 14, 0.96);
  border: 1px solid rgba(62, 233, 240, 0.5);
  border-radius: 0.15rem;
  box-shadow:
      0 0 0.9rem rgba(62, 233, 240, 0.2),
      inset 0 0 0.75rem rgba(62, 233, 240, 0.06);
  pointer-events: none;
}

.mod-tip__line {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin: 0;
}

.mod-tip__prompt {
  color: rgba(62, 233, 240, 0.6);
}

.mod-tip__name {
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.mod-tip__caret {
  width: 0.3rem;
  height: 0.56rem;
  background: var(--tip-cyan);
  animation: mod-tip-blink 1s steps(1, end) infinite;
}

.mod-tip__prose {
  margin: 0.2rem 0 0;
  font-size: 0.54rem;
  line-height: 1.55;
  color: rgba(62, 233, 240, 0.62);
}

.mod-tip__rows {
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
  margin: 0.35rem 0 0;
  padding: 0.3rem 0 0;
  list-style: none;
  border-top: 1px solid rgba(62, 233, 240, 0.18);
}

/* A dotted leader carries the eye from a variable-width stat name to its value,
   the way a printed readout column does — it is what keeps the numbers legible
   in a right-aligned stack that is never the same width twice. */
.mod-tip__row {
  display: flex;
  align-items: baseline;
  gap: 0.3rem;
}

.mod-tip__stat {
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
}

.mod-tip__leader {
  flex: 1 1 auto;
  min-width: 0.75rem;
  border-bottom: 1px dotted rgba(62, 233, 240, 0.3);
  transform: translateY(-0.15em);
}

.mod-tip__value {
  font-weight: 600;
  white-space: nowrap;
}

.mod-tip__value.is-up {
  color: var(--tip-up);
  text-shadow: 0 0 0.4rem rgba(0, 255, 106, 0.45);
}

.mod-tip__value.is-down {
  color: var(--tip-down);
  text-shadow: 0 0 0.4rem rgba(255, 23, 68, 0.45);
}

/* Enter only. Fading a readout out let it linger over the one opening beside it
   when the pointer crosses from tag to tag — two overlapping panels for a tenth
   of a second, which reads as a glitch. Leaving is immediate. */
.mod-tip-fade-enter-active {
  transition: opacity 0.12s ease;
}

.mod-tip-fade-enter-from {
  opacity: 0;
}

@keyframes mod-tip-blink {
  0%, 50% { opacity: 1; }
  50.01%, 100% { opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .mod-tip__caret {
    animation: none;
  }

  .mod-tip-fade-enter-active {
    transition: none;
  }
}
</style>
